import { CURRENCY } from "@core/utils/consts";
import { __t } from "@core/translation/translation";
import { getMarketsCatalogSnapshot } from "@core/utils/marketsCatalogStore";

const QUOTE_LABELS = {
  IRT: () => __t("toman"),
  USDT: () => __t("Tether"),
  CAD: () => "CAD",
};

const QUOTE_PRICE_SUFFIX = {
  IRT: () => __t("In Toman"),
  USDT: () => __t("In Tether"),
  CAD: () => "CAD",
};

function catalogCurrencyName(code) {
  const catalog = getMarketsCatalogSnapshot();
  const meta = catalog?.currencies?.[code];
  if (!meta) return null;
  return meta.name || meta.full_name || meta.fullname || meta.description || null;
}

export function getQuoteLabel(quote) {
  const key = String(quote || "").toUpperCase();
  return QUOTE_LABELS[key] ? QUOTE_LABELS[key]() : key;
}

export function getQuotePriceSuffix(quote) {
  const key = String(quote || "").toUpperCase();
  return QUOTE_PRICE_SUFFIX[key] ? QUOTE_PRICE_SUFFIX[key]() : key;
}

/** Icon/name metadata: CURRENCY.TYPE, then catalog, then code fallback. */
export function getCurrencyMeta(code) {
  const upper = String(code || "").toUpperCase();
  const known = CURRENCY?.TYPE?.[upper];

  if (known) {
    return {
      code: upper,
      name: known.NAME,
      unit: known.UNIT,
      color: known.COLOR,
      Icon: known.ICON,
      irtMarketKey: known.IR_MARKET || null,
    };
  }

  const fromCatalog = catalogCurrencyName(upper);

  return {
    code: upper,
    name: fromCatalog || upper,
    unit: upper,
    color: undefined,
    Icon: null,
    irtMarketKey: upper === "IRT" ? null : `${upper}_IRT`,
  };
}

export function parseMarketPair(marketKeyOrRoute) {
  const normalized = String(marketKeyOrRoute || "")
    .replace(/-/g, "_")
    .toUpperCase();
  const [base, quote] = normalized.split("_");
  return { base: base || "", quote: quote || "" };
}

/** Display labels for market tables and headers (supports BTC_IRT and BTC-IRT). */
export function getMarketPairDisplay(marketKeyOrRoute) {
  const { base, quote } = parseMarketPair(marketKeyOrRoute);
  const baseMeta = getCurrencyMeta(base);
  const quoteMeta = getCurrencyMeta(quote);
  const quoteName = QUOTE_LABELS[quote] ? getQuoteLabel(quote) : quoteMeta.name;

  return {
    base,
    quote,
    baseName: baseMeta.name,
    quoteName,
    baseUnit: baseMeta.unit,
    quoteUnit: quoteMeta.unit,
    baseMeta,
    quoteMeta,
  };
}

export function getMarketKey(base, quote) {
  return `${String(base || "").toUpperCase()}_${String(quote || "").toUpperCase()}`;
}

export function getIrtMarketKey(base) {
  return getCurrencyMeta(base).irtMarketKey;
}

/** Stats/order-book key e.g. BTC_IRT for any enabled base currency. */
export function getQuoteMarketKey(base, quote = "IRT") {
  const upperBase = String(base || "").toUpperCase();
  const upperQuote = String(quote || "").toUpperCase();
  if (!upperBase || upperBase === upperQuote) return null;

  const known = CURRENCY?.TYPE?.[upperBase];
  const fromKnown = known?.MARKETS?.[upperQuote];
  if (fromKnown) return fromKnown;

  return getMarketKey(upperBase, upperQuote);
}

/** Resolve ticker unitPrice from statMap with case-insensitive key match. */
export function getUnitPriceFromStats(stats, marketKey, fallback = 1) {
  if (!stats || !marketKey) return fallback;

  const normalized = String(marketKey).toUpperCase();
  const direct = stats[marketKey] ?? stats[normalized];
  if (direct?.unitPrice != null && Number.isFinite(direct.unitPrice)) {
    return direct.unitPrice;
  }

  const found = Object.entries(stats).find(
    ([key]) => String(key).toUpperCase() === normalized
  );
  const price = found?.[1]?.unitPrice;
  return price != null && Number.isFinite(price) ? price : fallback;
}

export function getBalanceRateInQuote(currencyName, stats, quote = "IRT") {
  const upper = String(currencyName || "").toUpperCase();
  const upperQuote = String(quote || "IRT").toUpperCase();
  if (upper === upperQuote) return 1;

  const marketKey = getQuoteMarketKey(upper, upperQuote);
  return getUnitPriceFromStats(stats, marketKey, 1);
}

export function getIconPath(code) {
  return `/static/icons/Logo-Crypto/${String(code || "").toUpperCase()}.svg`;
}
